/* eslint-disable import/order */
/* eslint-disable camelcase */
import { sentenceCase } from 'change-case';
import { useState, useEffect } from 'react';

import {
  Card,
  Button,
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import CRUD from 'utils/Crud';

import { PATH_DASHBOARD } from '../../routes/paths';

import useSettings from '../../hooks/useSettings';
import { useSnackbar } from 'notistack';

import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';

import { DataGrid } from '@mui/x-data-grid';
import { LoadingButton } from '@mui/lab';

import CustomGridToolbar from 'components/CustomGridToolbar';
import ProfileDialog from 'sections/@dashboard/user/ProfileDialog';
import Label from 'components/Label';
import UserKYCDialog from 'sections/@dashboard/user/UserKYCDialog';

export default function ComplianceQueue() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [sort, setSort] = useState({
    page: 1,
    page_size: 50,
    type: 'needs_review',
    stage: '',
    risk_level: ''
  });

  const [data, setData] = useState({
    data: [],
    current_page: 1,
    total_pages: 1,
    total_size: 0,
    page_size: 50
  });

  const [state, setState] = useState({
    loading: false,
    error: null
  });

  const [profileDialog, setProfileDialog] = useState({ open: false, user: null });
  const [kyc, setKyc] = useState({ open: false, userId: null });

  useEffect(() => {
    getInitData(1);
  }, []);

  // =====================================================
  // FETCH DATA
  // =====================================================
  async function getInitData(page = 1) {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const query =
        `?type=${sort.type}` +
        `&page=${page}` +
        `&page_size=${sort.page_size}` +
        `&stage=${sort.stage}` +
        `&risk_level=${sort.risk_level}`;

      const response = await axios.get(CRUD.compliance.queue + query);

      if (isMountedRef.current) {
        setData(response.data);
      }

      setState({ loading: false, error: null });
    } catch (error) {
      setState({
        loading: false,
        error: getErrorMessage(error)
      });
    }
  }

  // =====================================================
  // STATUS RENDER
  // =====================================================
  function renderStatus(value, successValue) {
    let color = 'default';
    let label = 'N/A';

    if (!value) {
      label = 'N/A';
    } else if (value === successValue) {
      color = 'success';
      label = sentenceCase(value);
    } else if (value === 'pending') {
      color = 'warning';
      label = 'Pending';
    } else if (value === 'failed' || value === 'rejected') {
      color = 'error';
      label = sentenceCase(value);
    } else {
      label = sentenceCase(value);
    }

    return <Label color={color}>{label}</Label>;
  }

  return (
    <Page title="Compliance Queue">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Compliance Queue"
          links={[
            { name: 'Compliance', href: PATH_DASHBOARD.compliance.root },
            { name: 'Queue' },
          ]}
        />

        {state.error && (
          <Card>
            <Box p={5} textAlign="center">
              <Typography color="error">{state.error}</Typography>
            </Box>
          </Card>
        )}

        <Card>

          {/* ================= FILTERS ================= */}
          <Box sx={{ display: "flex", justifyContent: "end", p: 2 }}>

            <FormControl sx={{ ml: 2, width: 180 }}>
              <InputLabel>Queue</InputLabel>
              <Select
                value={sort.type}
                label="Queue"
                onChange={(e) => setSort({ ...sort, type: e.target.value })}
              >
                <MenuItem value="needs_review">Needs Review</MenuItem>
                <MenuItem value="flagged">Flagged</MenuItem>
                <MenuItem value="high_risk">High Risk</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ ml: 2, width: 180 }}>
              <InputLabel>Stage</InputLabel>
              <Select
                value={sort.stage}
                label="Stage"
                onChange={(e) => setSort({ ...sort, stage: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="stage_one">Stage 1</MenuItem>
                <MenuItem value="stage_two">Stage 2</MenuItem>
                <MenuItem value="stage_three">Stage 3</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ ml: 2, width: 180 }}>
              <InputLabel>Risk</InputLabel>
              <Select
                value={sort.risk_level}
                label="Risk"
                onChange={(e) => setSort({ ...sort, risk_level: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <LoadingButton
              loading={state.loading}
              onClick={() => getInitData(1)}
              sx={{ ml: 2 }}
              variant="contained"
            >
              Apply
            </LoadingButton>

          </Box>

          {/* ================= TABLE ================= */}
          <Box sx={{ height: 650, width: '100%' }}>
            <DataGrid
              rows={data.data}
              getRowId={(row) => row.id}

              columns={[
                {
                  field: 'user',
                  headerName: 'User',
                  width: 260,
                  renderCell: (params) => (
                    <Box display="flex" alignItems="center">
                      <IconButton
                        onClick={() =>
                          setProfileDialog({ open: true, user: { user_id: params.row.user_id } })
                        }
                      >
                        <Iconify icon={'ph:user'} />
                      </IconButton>

                      <Box ml={1}>
                        <Typography
                          variant="subtitle2"
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() =>
                            setKyc({ open: true, userId: params.row.user_id })
                          }
                        >
                          {params.row.first_name} {params.row.last_name}
                        </Typography>

                        <Typography variant="caption">
                          {params.row.user_id.slice(0, 10)}...
                        </Typography>
                      </Box>
                    </Box>
                  ),
                },

                {
                  field: 'identity_status',
                  headerName: 'Identity',
                  width: 120,
                  renderCell: (params) => renderStatus(params.value, 'verified'),
                },

                {
                  field: 'bvn_status',
                  headerName: 'BVN',
                  width: 120,
                  renderCell: (params) => renderStatus(params.value, 'verified'),
                },

                {
                  field: 'address_status',
                  headerName: 'Address',
                  width: 130,
                  renderCell: (params) => renderStatus(params.value, 'approved'),
                },

                {
                  field: 'risk_level',
                  headerName: 'Risk',
                  width: 120,
                  renderCell: (params) => {
                    const color =
                      params.value === 'high'
                        ? 'error'
                        : params.value === 'medium'
                        ? 'warning'
                        : 'success';

                    return <Label color={color}>{params.value || 'low'}</Label>;
                  },
                },

                {
                  field: 'confidence_score',
                  headerName: 'Confidence',
                  width: 130,
                  renderCell: (params) => (
                    <Typography variant="caption">
                      {params.value
                        ? `${Math.round(params.value * 100)}%`
                        : '—'}
                    </Typography>
                  ),
                },

                {
                  field: 'flags',
                  headerName: 'Flags',
                  width: 120,
                  renderCell: (params) =>
                    params.value ? (
                      <Label color="error">Flagged</Label>
                    ) : (
                      <Label>Clean</Label>
                    ),
                },

                {
                  field: 'updated_at',
                  headerName: 'Updated',
                  width: 180,
                },
              ]}

              loading={state.loading}
              components={{ Toolbar: CustomGridToolbar }}

              paginationMode="server"
              rowCount={data.total_size}
              page={data.current_page - 1}
              pageSize={data.page_size}

              onPageChange={(newPage) => {
                const page = newPage + 1;
                setSort((prev) => ({ ...prev, page }));
                getInitData(page);
              }}

              onPageSizeChange={(newSize) => {
                setSort((prev) => ({ ...prev, page_size: newSize, page: 1 }));
                getInitData(1);
              }}

              getRowClassName={(params) => {
                if (params.row.risk_level === 'high') return 'row-high-risk';
                if (params.row.flags) return 'row-flagged';
                return '';
              }}
            />
          </Box>
        </Card>
      </Container>

      {/* ================= DIALOGS ================= */}

      {profileDialog.open && (
        <ProfileDialog
          user={profileDialog.user}
          refresh
          handleClose={() => setProfileDialog({ open: false })}
        />
      )}

      <UserKYCDialog
        open={kyc.open}
        userID={kyc.userId}
        handleClose={() => setKyc({ open: false, userId: null })}
      />
    </Page>
  );
}